import dayjs from "dayjs";
import { TFunction } from "i18next";
import * as yup from "yup";
import { InferType } from "yup";
import { USStateCode } from "@/utils/us-states.enum";

const registerParticipantValidationSchema = (
  t: TFunction,
  country: string,
  dateOfBirth: boolean = false,
  phoneMandatory: boolean = false,
  minAgeAllowed: number = 18,
  cinMandatory: boolean = true,
) => {
  return yup.lazy(() => {
    const dateOfBirthValidation = (): yup.StringSchema<
      string | undefined | null
    > => {
      const baseSchema = dateOfBirth
        ? yup.string().required(t("validations.required"))
        : yup.string().optional().nullable();

      return baseSchema
        .test(
          "futureDate",
          "Date of birth cannot be in the future",
          (value) => {
            if (!value) return true;
            return dayjs(value).isBefore(dayjs());
          },
        )
        .test(
          "minAge",
          t("validations.validator_birth_date", { minAgeAllowed }),
          (value) => {
            if (!value) return true;
            const age = dayjs().diff(dayjs(value), "year");
            return age >= minAgeAllowed;
          },
        );
    };

    const localeValidation = (): yup.StringSchema => {
      switch (country) {
        case "US":
          return yup
            .string()
            .test(
              "min",
              t("validations.validator_digits_min", { count: 10 }),
              (value) => {
                if (!value || value.length === 0) return true;
                return value.length >= 10;
              },
            )
            .test(
              "max",
              t("validations.validator_digits_max", { count: 10 }),
              (value) => {
                if (!value || value.length === 0) return true;
                return value.length <= 10;
              },
            )
            .test("phoneNumber", t("validations.invalid_format"), (value) => {
              if (!value || value.trim() === "") return true;
              const phoneRegex = /^\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/;
              return phoneRegex.test(value);
            });
        case "UK":
          return yup
            .string()
            .test(
              "min",
              t("validations.validator_digits_min", { count: 10 }),
              (value) => {
                if (!value || value.length === 0) return true;
                return value.length >= 10;
              },
            )
            .test(
              "max",
              t("validations.validator_digits_max", { count: 10 }),
              (value) => {
                if (!value || value.length === 0) return true;
                return value.length <= 10;
              },
            )
            .test("phoneNumber", t("validations.invalid_format"), (value) => {
              if (!value || value.trim() === "") return true;
              const phoneRegex = /^\(?\d{4}\)?[-. ]?\d{6}$/;
              return phoneRegex.test(value);
            });
        default:
          return yup.string();
      }
    };

    // Address validation helpers based on country
    const getAddressLine1Validation = () => {
      return yup
        .string()
        .nullable()
        .notRequired()
        .transform((value) => (value === "" ? null : value))
        .test(
          "alphanumeric",
          "Valid characters: Alphanumeric and common symbols",
          (value) => {
            if (!value) return true;
            // Allow alphanumeric and common symbols (spaces, hyphens, periods, commas, #, etc.)
            const regex = /^[a-zA-Z0-9\s\-.,#/()]+$/;
            return regex.test(value);
          },
        )
        .min(5, t("validations.min_length", { count: 5 }))
        .max(100, t("validations.max_length", { count: 100 }));
    };

    const getAddressLine2Validation = () => {
      return yup
        .string()
        .nullable()
        .notRequired()
        .transform((value) => (value === "" ? null : value))
        .test(
          "alphanumeric",
          "Valid characters: Alphanumeric and common symbols",
          (value) => {
            if (!value) return true;
            const regex = /^[a-zA-Z0-9\s\-.,#/()]+$/;
            return regex.test(value);
          },
        )
        .max(100, t("validations.max_length", { count: 100 }));
    };

    const getCityValidation = () => {
      return yup
        .string()
        .nullable()
        .notRequired()
        .transform((value) => (value === "" ? null : value))
        .min(3, t("validations.min_length", { count: 3 }))
        .max(100, t("validations.max_length", { count: 100 }));
    };

    const getStateValidation = () => {
      if (country === "US") {
        return yup
          .string()
          .nullable()
          .notRequired()
          .transform((value) => (value === "" ? null : value))
          .test("validUSState", "Please select a valid US state", (value) => {
            if (!value) return true;
            const validCodes = Object.values(USStateCode);
            return validCodes.includes(value as USStateCode);
          });
      }
      return yup
        .string()
        .nullable()
        .notRequired()
        .transform((value) => (value === "" ? null : value));
    };

    const getZipCodeValidation = () => {
      if (country === "US") {
        return yup
          .string()
          .nullable()
          .notRequired()
          .transform((value) => (value === "" ? null : value))
          .min(5, t("validations.min_length", { count: 5 }))
          .max(10, t("validations.max_length", { count: 10 }));
      }
      // UK or non-US
      return yup
        .string()
        .nullable()
        .notRequired()
        .transform((value) => (value === "" ? null : value))
        .min(5, t("validations.min_length", { count: 5 }))
        .max(8, t("validations.max_length", { count: 8 }));
    };

    const getCountyValidation = () => {
      return yup
        .string()
        .nullable()
        .notRequired()
        .transform((value) => (value === "" ? null : value))
        .max(100, t("validations.max_length", { count: 100 }));
    };

    const schemaFields: yup.ObjectShape = {
      firstName: yup
        .string()
        .required(t("validations.required"))
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      lastName: yup
        .string()
        .required(t("validations.required"))
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      email: yup
        .string()
        .required(t("validations.required"))
        .email(t("validations.invalid_format"))
        .max(100, t("validations.max_length", { count: 100 })),
      dateOfBirth: dateOfBirthValidation(),
      phoneNumber: phoneMandatory
        ? localeValidation().required(t("validations.required"))
        : localeValidation().optional(),
      cin: cinMandatory
        ? yup
            .string()
            .required(t("validations.required"))
            .min(2, t("validations.min_length", { count: 2 }))
            .max(50, t("validations.max_length", { count: 50 }))
            .test("regexValidation", t("validations.cin-regex"), (value) => {
              if (!value || value.trim() === "") return true;
              // Allow alphanumeric characters and common symbols (spaces, hyphens, periods, commas, #, /, etc.)
              const regex = /^[a-zA-Z0-9\s\-.,#/()]+$/;
              return regex.test(value);
            })
        : yup
            .string()
            .optional()
            .transform((value) =>
              value === "" || value === null || value === undefined
                ? ""
                : value,
            )
            .test("min", t("validations.min_length", { count: 2 }), (value) => {
              if (!value || value.trim() === "") return true;
              return value.length >= 2;
            })
            .test(
              "max",
              t("validations.max_length", { count: 50 }),
              (value) => {
                if (!value || value.trim() === "") return true;
                return value.length <= 50;
              },
            )
            .test("regexValidation", t("validations.cin-regex"), (value) => {
              if (!value || value.trim() === "") return true;
              // Allow alphanumeric characters and common symbols (spaces, hyphens, periods, commas, #, /, etc.)
              const regex = /^[a-zA-Z0-9\s\-.,#/()]+$/;
              return regex.test(value);
            }),
      // Primary Address fields with country-specific validations
      primaryAddressLine1: getAddressLine1Validation(),
      primaryAddressLine2: getAddressLine2Validation(),
      primaryCity: getCityValidation(),
      primaryState: getStateValidation(),
      primaryZipCode: getZipCodeValidation(),
      primaryCounty: getCountyValidation(),
      // Secondary Address fields with country-specific validations
      secondaryAddressLine1: getAddressLine1Validation(),
      secondaryAddressLine2: getAddressLine2Validation(),
      secondaryCity: getCityValidation(),
      secondaryState: getStateValidation(),
      secondaryZipCode: getZipCodeValidation(),
      secondaryCounty: getCountyValidation(),
      // Legacy fields (for backward compatibility)
      house: yup
        .string()
        .nullable()
        .notRequired()
        .transform((value) => (value === "" ? null : value))
        .min(10, t("validations.min_length", { count: 10 }))
        .max(250, t("validations.max_length", { count: 250 })),
      locality: yup
        .string()
        .nullable()
        .notRequired()
        .transform((value) => (value === "" ? null : value))
        .min(3, t("validations.min_length", { count: 3 }))
        .max(250, t("validations.max_length", { count: 250 })),
      postalCode: yup
        .string()
        .nullable()
        .notRequired()
        .transform((value) => (value === "" ? null : value))
        .min(5, t("validations.min_length", { count: 5 }))
        .max(10, t("validations.max_length", { count: 10 })),
    };

    // Note: status is not editable in the form, it's maintained from the original participant
    // and sent in the payload, but it's not part of the form validation

    return yup
      .object()
      .shape(schemaFields)
      .test(
        "address-consistency",
        "Address block consistency validation",
        function (values) {
          const {
            primaryAddressLine1,
            primaryCity,
            primaryState,
            primaryZipCode,
            secondaryAddressLine1,
            secondaryCity,
            secondaryState,
            secondaryZipCode,
          } = values as any;

          // AC07: Priority between Primary Address and Secondary Address
          const hasSecondaryAddress = !!(
            secondaryAddressLine1 ||
            secondaryCity ||
            secondaryState ||
            secondaryZipCode
          );
          const hasPrimaryAddress = !!(
            primaryAddressLine1 ||
            primaryCity ||
            primaryState ||
            primaryZipCode
          );

          if (hasSecondaryAddress && !hasPrimaryAddress) {
            return this.createError({
              path: "secondaryAddressLine1",
              message:
                "You need to fill out the Primary Address first before entering a Secondary Address",
            });
          }

          // AC06: Address block consistency - Primary Address
          // Required fields depend on country
          if (hasPrimaryAddress) {
            if (!primaryAddressLine1) {
              return this.createError({
                path: "primaryAddressLine1",
                message: t("validations.required"),
              });
            }
            if (!primaryCity) {
              return this.createError({
                path: "primaryCity",
                message: t("validations.required"),
              });
            }
            // State is required for US, County is optional for UK/non-US
            if (country === "US" && !primaryState) {
              return this.createError({
                path: "primaryState",
                message: t("validations.required"),
              });
            }
            if (!primaryZipCode) {
              return this.createError({
                path: "primaryZipCode",
                message: t("validations.required"),
              });
            }
          }

          // AC06: Address block consistency - Secondary Address
          if (hasSecondaryAddress) {
            if (!secondaryAddressLine1) {
              return this.createError({
                path: "secondaryAddressLine1",
                message: t("validations.required"),
              });
            }
            if (!secondaryCity) {
              return this.createError({
                path: "secondaryCity",
                message: t("validations.required"),
              });
            }
            // State is required for US, County is optional for UK/non-US
            if (country === "US" && !secondaryState) {
              return this.createError({
                path: "secondaryState",
                message: t("validations.required"),
              });
            }
            if (!secondaryZipCode) {
              return this.createError({
                path: "secondaryZipCode",
                message: t("validations.required"),
              });
            }
          }

          return true;
        },
      );
  });
};

export type ParticipantRegisterFormData = InferType<
  ReturnType<typeof registerParticipantValidationSchema>
>;

export default registerParticipantValidationSchema;
